"use client";

import { useState } from "react";
import { Link } from "@/components/ui/Link";
import { Container } from "@/components/ui/Container";
import { Reveal } from "@/components/ui/Reveal";
import { SectionLabel } from "@/components/ui/SectionLabel";
import { SplitHeading } from "@/components/ui/SplitHeading";
import { SECTION_TITLE_CLASS } from "@/lib/typography";
import type { Industry } from "@/lib/content/types";

type IndustriesProps = {
  industries: Industry[];
  label?: string;
  title?: string;
  description?: string;
  viewAllLabel?: string;
};

export function Industries({
  industries,
  label,
  title,
  description,
  viewAllLabel,
}: IndustriesProps) {
  const [activeSlug, setActiveSlug] = useState(industries[0]?.slug ?? "");
  const active =
    industries.find((item) => item.slug === activeSlug) ?? industries[0];

  if (!industries.length || !title) return null;

  return (
    <section id="industries" className="py-20 md:py-28 lg:py-32">
      <Container>
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="lg:col-span-4">
            <Reveal>
              {label ? <SectionLabel className="mb-5">{label}</SectionLabel> : null}
              <SplitHeading className={SECTION_TITLE_CLASS}>
                {title}
              </SplitHeading>
              {description ? (
                <p className="mt-6 max-w-sm text-base leading-relaxed text-muted md:text-lg">
                  {description}
                </p>
              ) : null}
              {viewAllLabel ? (
                <Link
                  href="/industries"
                  title={viewAllLabel}
                  className="mt-8 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-foreground"
                >
                  {viewAllLabel}
                  <span aria-hidden="true">→</span>
                </Link>
              ) : null}
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <div className="flex flex-wrap content-start gap-3">
              {industries.map((industry, index) => {
                const isActive = industry.slug === active?.slug;
                return (
                  <Reveal key={industry.slug} delay={index * 0.02}>
                    <button
                      type="button"
                      onClick={() => setActiveSlug(industry.slug)}
                      onMouseEnter={() => setActiveSlug(industry.slug)}
                      aria-pressed={isActive}
                      className={`inline-flex items-center rounded-full px-5 py-2.5 text-sm transition-colors ring-1 ring-inset ${
                        isActive
                          ? "bg-white/[0.12] text-foreground ring-white/20"
                          : "bg-white/[0.06] text-foreground/85 ring-white/[0.06] hover:bg-white/[0.1] hover:text-foreground"
                      }`}
                    >
                      {industry.name}
                    </button>
                  </Reveal>
                );
              })}
            </div>

            {active?.description ? (
              <div className="mt-8 border-t border-white/[0.08] pt-6">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted">
                  {active.name}
                </p>
                <p className="mt-3 max-w-xl text-base leading-relaxed text-muted md:text-lg">
                  {active.description}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </Container>
    </section>
  );
}
